import React, { useState, useEffect } from 'react';
import { 
  Users, Shield, Lock, Unlock, 
  Trash2, Plus, Mail, Check, X,
  UserCheck, ShieldCheck, UsersRound, SquarePen, Eye, UserPlus,
  UserCog, Globe, User
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from "../ui/table";
import { 
  Card, CardContent, CardDescription, CardHeader, CardTitle 
} from "../ui/card";
import { cn } from "../ui/utils";
import MemberSelectModal from "./MemberSelectModal";
import { toast } from "sonner";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter
} from "../ui/dialog";
import { getRandomAvatarColor, getUserInitials } from "../../utils/avatar-utils";
import { serviceDeskApi } from "../../utils/service-desk-api";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";

interface Member {
  id: string;
  name: string;
  email: string;
  role: string;
  initials: string;
  color: string;
  joinedDate: string;
}

export const ServiceDeskTeamSettings: React.FC<{ deskTitle: string, deskId: string }> = ({ deskTitle, deskId }) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [selectedRole, setSelectedRole] = useState<string>('VIEWER');
  const [desk, setDesk] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isEligible = (userPermissions: string[] | undefined, role: string) => {
    if (!userPermissions) return false;
    
    // Global Admin has all permissions
    const hasGlobalAdmin = userPermissions.includes(`${ACTIONS.MANAGE}:${RESOURCES.SERVICE_DESK_ADMIN}`);
    if (hasGlobalAdmin) return true;

    const hasTeamAdmin = userPermissions.includes(`${ACTIONS.MANAGE}:${RESOURCES.SERVICE_DESK_TEAM_ADMIN}`);
    const hasAgent = userPermissions.includes(`${ACTIONS.MANAGE}:${RESOURCES.SERVICE_DESK_AGENT}`);
    const hasViewer = userPermissions.includes(`${ACTIONS.MANAGE}:${RESOURCES.SERVICE_DESK_AGENT_VIEWER}`);

    switch (role) {
      case 'TEAM_ADMIN':
        return hasTeamAdmin;
      case 'EDITOR':
        return hasAgent || hasTeamAdmin;
      case 'VIEWER':
        return hasViewer || hasAgent || hasTeamAdmin;
      default:
        return false;
    }
  };

  const fetchDeskDetails = async () => {
    setIsLoading(true);
    try {
        const [deskResponse, membersResponse] = await Promise.all([
          serviceDeskApi.getServiceDesk(deskId),
          serviceDeskApi.getServiceDeskMembers(deskId)
        ]);

        if (deskResponse.success) {
            const deskData = deskResponse.desk;
            // Inject members with permissions from the dedicated endpoint
            if (membersResponse.success) {
              deskData.members = membersResponse.members;
            }
            setDesk(deskData);
        }
    } catch (error) {
        console.error("Error fetching desk details:", error);
        toast.error("Failed to load team settings.");
    } finally {
        setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDeskDetails();
  }, [deskId]);

  const handleUserSelect = (user: any) => {
    const isAlreadyMember = (desk.members || []).some((m: any) => String(m.user?.id) === String(user.id));
    if (isAlreadyMember) {
      toast.error(`${user.name} is already a member of this service desk.`);
      return;
    }
    setSelectedUser(user);
    setIsAddModalOpen(false);
    setIsRoleModalOpen(true);
  };

  const handleConfirmAdd = async () => {
    if (!selectedUser || !desk) return;

    try {
        let teamAdminId = desk.teamAdmin?.id;
        const newMembersList = (desk.members || []).map((m: any) => ({ 
            userId: m.user?.id, 
            role: (selectedRole === 'TEAM_ADMIN' && m.role === 'TEAM_ADMIN') ? 'EDITOR' : m.role 
        }));
        
        if (selectedRole === 'TEAM_ADMIN') {
            if (teamAdminId && !newMembersList.some((m: any) => String(m.userId) === String(teamAdminId))) {
                newMembersList.push({ userId: teamAdminId, role: 'EDITOR' });
            }
            teamAdminId = selectedUser.id;
        }

        newMembersList.push({ userId: selectedUser.id, role: selectedRole });

        const success = await serviceDeskApi.updateServiceDesk(deskId, { 
            members: newMembersList,
            teamAdminId 
        });

        if (success) {
            await fetchDeskDetails();
            setIsRoleModalOpen(false);
            setSelectedUser(null);
            setSelectedRole('VIEWER');
            toast.success(`${selectedUser.name} added successfully.`);
        }
    } catch (error) {
        toast.error("Failed to add member.");
    }
  };

  const handleRoleChange = async (userId: string, newRole: string) => {
    if (!desk) return;
    try {
        let teamAdminId = desk.teamAdmin?.id;
        const newMembersList = desk.members.map((m: any) => ({
            userId: m.user?.id,
            role: m.user?.id === userId 
                ? newRole 
                : (newRole === 'TEAM_ADMIN' && m.role === 'TEAM_ADMIN' ? 'EDITOR' : m.role)
        }));

        if (newRole === 'TEAM_ADMIN') {
            teamAdminId = userId;
        }

        const success = await serviceDeskApi.updateServiceDesk(deskId, { 
            members: newMembersList,
            teamAdminId
        });
        
        if (success) {
            await fetchDeskDetails();
            toast.success("Role updated.");
        }
    } catch (error) {
        toast.error("Failed to update role.");
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!desk) return;
    try {
        const newMembersList = desk.members
            .filter((m: any) => m.user?.id !== userId)
            .map((m: any) => ({ userId: m.user?.id, role: m.role }));

        const success = await serviceDeskApi.updateServiceDesk(deskId, { members: newMembersList });
        if (success) {
            await fetchDeskDetails();
            toast.success("Member removed.");
        }
    } catch (error) {
        toast.error("Failed to remove member.");
    }
  };

  if (isLoading) {
      return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  if (!desk) return null;

  const roleLabels: Record<string, string> = {
      'TEAM_ADMIN': 'Team Admin',
      'EDITOR': 'Editor',
      'VIEWER': 'Viewer'
  };

  return (
    <div className="max-w-7xl mx-auto pb-20">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold mb-2">Team Settings</h2>
          <p className="text-muted-foreground">Manage team members and their roles for {desk.name}</p>
        </div>
        <Button onClick={() => setIsAddModalOpen(true)} className="shadow-sm">
          <UserPlus className="mr-2 h-4 w-4" /> Add Member
        </Button>
      </div>

      {isAddModalOpen && (
        <MemberSelectModal 
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onAdd={handleUserSelect}
          deskTitle={desk.name}
          title="Add Team Member"
          selectedIds={(desk.members || []).map((m: any) => String(m.user?.id))}
        />
      )}

      {/* Role Selection Modal */}
      {isRoleModalOpen && (
        <Dialog open={isRoleModalOpen} onOpenChange={setIsRoleModalOpen}>
          <DialogContent className="p-0 sm:max-w-md border-none overflow-hidden bg-white shadow-xl">
            <DialogHeader className="p-6 border-b border-border flex-row items-center gap-3 space-y-0">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Shield className="w-5 h-5 text-primary" />
              </div>
              <DialogTitle className="text-xl font-bold">Assign Role</DialogTitle>
            </DialogHeader>

            <div className="p-6">
              <DialogDescription className="text-sm text-muted-foreground mb-4">
                Step 2: Assign a role for {selectedUser?.name} in {desk.name}
              </DialogDescription>

              <div className="space-y-3">
                {isEligible(selectedUser?.permissions, 'VIEWER') && (
                  <button 
                    onClick={() => setSelectedRole('VIEWER')}
                    className={cn(
                      "w-full flex items-center gap-4 p-4 border rounded-xl transition-all text-left",
                      selectedRole === 'VIEWER' 
                        ? "bg-primary/5 border-primary shadow-sm ring-1 ring-primary" 
                        : "bg-card border-border hover:border-primary/50"
                    )}
                  >
                    <div className={cn(
                      "p-2 rounded-lg",
                      selectedRole === 'VIEWER' ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                    )}>
                      <Eye className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="font-bold">Viewer</div>
                      <div className="text-sm text-muted-foreground">Can view requests and comments only.</div>
                    </div>
                    {selectedRole === 'VIEWER' && <Check className="w-5 h-5 text-primary" />}
                  </button>
                )}

                {isEligible(selectedUser?.permissions, 'EDITOR') && (
                  <button 
                    onClick={() => setSelectedRole('EDITOR')}
                    className={cn(
                      "w-full flex items-center gap-4 p-4 border rounded-xl transition-all text-left",
                      selectedRole === 'EDITOR' 
                        ? "bg-blue-50 border-blue-200 shadow-sm ring-1 ring-blue-500" 
                        : "bg-card border-border hover:border-blue-500/50"
                    )}
                  >
                    <div className={cn(
                      "p-2 rounded-lg",
                      selectedRole === 'EDITOR' ? "bg-blue-100 text-blue-600" : "bg-muted text-muted-foreground"
                    )}>
                      <SquarePen className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="font-bold">Editor</div>
                      <div className="text-sm text-muted-foreground">Can manage requests, add comments, and change status.</div>
                    </div>
                    {selectedRole === 'EDITOR' && <Check className="w-5 h-5 text-blue-600" />}
                  </button>
                )}

                {isEligible(selectedUser?.permissions, 'TEAM_ADMIN') && (
                  <button 
                    onClick={() => setSelectedRole('TEAM_ADMIN')}
                    className={cn(
                      "w-full flex items-center gap-4 p-4 border rounded-xl transition-all text-left",
                      selectedRole === 'TEAM_ADMIN' 
                        ? "bg-purple-50 border-purple-200 shadow-sm ring-1 ring-purple-500" 
                        : "bg-card border-border hover:border-purple-500/50"
                    )}
                  >
                    <div className={cn(
                      "p-2 rounded-lg",
                      selectedRole === 'TEAM_ADMIN' ? "bg-purple-100 text-purple-600" : "bg-muted text-muted-foreground"
                    )}>
                      <Shield className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="font-bold">Team Admin</div>
                      <div className="text-sm text-muted-foreground">Full access to manage requests, forms, and team members.</div>
                    </div>
                    {selectedRole === 'TEAM_ADMIN' && <Check className="w-5 h-5 text-purple-600" />}
                  </button>
                )}

                {!isEligible(selectedUser?.permissions, 'VIEWER') && !isEligible(selectedUser?.permissions, 'EDITOR') && !isEligible(selectedUser?.permissions, 'TEAM_ADMIN') && (
                  <div className="p-8 text-center border-2 border-dashed border-red-200 rounded-xl bg-red-50">
                    <X className="w-10 h-10 text-red-400 mx-auto mb-2" />
                    <div className="text-red-700 font-bold">User Not Eligible</div>
                    <p className="text-red-600 text-sm">This user does not have any Service Desk permissions assigned globally.</p>
                  </div>
                )}
              </div>
            </div>

            <DialogFooter className="p-6 border-t border-border flex items-center justify-end gap-3 bg-muted/30">
              <Button variant="ghost" onClick={() => { setIsRoleModalOpen(false); setIsAddModalOpen(true); }}>
                Back
              </Button>
              <Button onClick={handleConfirmAdd} className="px-6">
                Confirm & Add
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Access Summary */}
      <div className="bg-card border border-border rounded-lg p-4 mb-6 shadow-sm">
        <h3 className="text-lg font-bold mb-4">Access Summary</h3>
        <div className="space-y-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">Backend Access (Sidebar & Management):</span>
            </div>
            <div className="ml-6">
              <div className="flex items-center gap-2 text-sm font-bold text-purple-600">
                  <ShieldCheck className="w-4 h-4" />
                  <span>Limited to Team Administrator and Team Members only</span>
              </div>
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-2">
              <UserCheck className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">Request Submission Access:</span>
            </div>
            <div className="ml-6">
              {desk.submissionControl === 'open' ? (
                  <div className="flex items-center gap-2 text-sm font-bold text-green-600">
                      <Globe className="w-4 h-4" />
                      <span>Open to all employees with Requester permission</span>
                  </div>
              ) : (
                  <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-bold text-amber-600">
                          <Lock className="w-4 h-4" />
                          <span>Restricted to Authorized Requesters ({desk.authorizedRequesters?.length || 0})</span>
                      </div>
                  </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Role Permissions */}
      <div className="bg-card border border-border rounded-lg p-4 mb-6 shadow-sm">
        <h3 className="text-lg font-bold mb-4">Role Permissions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Team Admin */}
          <div className="flex gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-md border bg-purple-100 text-purple-700 border-purple-200 h-fit shrink-0">
              <Shield className="w-4 h-4" />
              <span className="text-sm font-bold">Team Admin</span>
            </div>
            <div className="flex-1">
              <ul className="text-sm space-y-1">
                <li className="text-muted-foreground">• Manage requests</li>
                <li className="text-muted-foreground">• Create & edit forms</li>
                <li className="text-muted-foreground">• Manage team members</li>
                <li className="text-muted-foreground">• Change roles</li>
              </ul>
            </div>
          </div>

          {/* Editor */}
          <div className="flex gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-md border bg-blue-100 text-blue-700 border-blue-200 h-fit shrink-0">
              <SquarePen className="w-4 h-4" />
              <span className="text-sm font-bold">Editor</span>
            </div>
            <div className="flex-1">
              <ul className="text-sm space-y-1">
                <li className="text-muted-foreground">• Manage requests</li>
                <li className="text-muted-foreground">• Add comments (public & private)</li>
                <li className="text-muted-foreground">• Change status & assign</li>
              </ul>
            </div>
          </div>

          {/* Viewer */}
          <div className="flex gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-md border bg-gray-100 text-gray-700 border-gray-200 h-fit shrink-0">
              <Eye className="w-4 h-4" />
              <span className="text-sm font-bold">Viewer</span>
            </div>
            <div className="flex-1">
              <ul className="text-sm space-y-1">
                <li className="text-muted-foreground">• View requests</li>
                <li className="text-muted-foreground">• View comments</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Members Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="text-left px-6 py-3 text-sm font-bold text-gray-700">Member</th>
                <th className="text-left px-6 py-3 text-sm font-bold text-gray-700">Email</th>
                <th className="text-left px-6 py-3 text-sm font-bold text-gray-700">Role</th>
                <th className="text-left px-6 py-3 text-sm font-bold text-gray-700">Joined</th>
                <th className="text-left px-6 py-3 text-sm font-bold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border bg-white">
              {(desk.members || []).map((member: any) => (
                <tr key={member.id} className="hover:bg-accent/50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className={cn("w-10 h-10 rounded-full flex items-center justify-center shadow-sm text-white font-bold", getRandomAvatarColor(member.user?.name))}>
                        <span className="font-bold text-sm">{getUserInitials(member.user?.name)}</span>
                      </div>
                      <span className="font-bold text-gray-900">{member.user?.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">
                    {member.user?.email}
                  </td>
                  <td className="px-6 py-4">
                    <div className={cn(
                      "inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm font-bold shadow-sm",
                      member.role === 'TEAM_ADMIN' ? "bg-purple-100 text-purple-700 border-purple-200" :
                      member.role === 'EDITOR' ? "bg-blue-100 text-blue-700 border-blue-200" :
                      "bg-gray-100 text-gray-700 border-gray-200"
                    )}>
                      {member.role === 'TEAM_ADMIN' && <Shield className="w-4 h-4" />}
                      {member.role === 'EDITOR' && <SquarePen className="w-4 h-4" />}
                      {member.role === 'VIEWER' && <Eye className="w-4 h-4" />}
                      <span>{roleLabels[member.role] || member.role}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">
                    {member.createdAt ? new Date(member.createdAt).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                        <select 
                            className="h-9 w-[140px] pr-10 pl-3 border border-border rounded-md bg-background hover:bg-accent transition-colors text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-50 disabled:cursor-not-allowed appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2020%2020%22%3E%3Cpath%20stroke%3D%22%236b7280%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20stroke-width%3D%221.5%22%20d%3D%22m6%208%204%204%204-4%22%2F%3E%3C%2Fsvg%3E')] bg-[length:1rem_1rem] bg-[right_12px_center] bg-no-repeat"
                            value={member.role}
                            onChange={(e) => handleRoleChange(member.user?.id, e.target.value)}
                            disabled={String(member.user?.id) === String(desk.teamAdmin?.id)}
                        >
                            {isEligible(member.user?.permissions, 'VIEWER') && <option value="VIEWER">Viewer</option>}
                            {isEligible(member.user?.permissions, 'EDITOR') && <option value="EDITOR">Editor</option>}
                            {isEligible(member.user?.permissions, 'TEAM_ADMIN') && <option value="TEAM_ADMIN">Team Admin</option>}
                            {!isEligible(member.user?.permissions, 'VIEWER') && !isEligible(member.user?.permissions, 'EDITOR') && !isEligible(member.user?.permissions, 'TEAM_ADMIN') && (
                                <option value={member.role}>No Global Permissions</option>
                            )}
                        </select>
                        
                        <Button 
                            variant="ghost" 
                            size="icon" 
                            className={cn(
                                "text-gray-400 hover:text-red-600",
                                String(member.user?.id) === String(desk.teamAdmin?.id) && "invisible"
                            )}
                            onClick={() => handleRemoveMember(member.user?.id)}
                            disabled={String(member.user?.id) === String(desk.teamAdmin?.id)}
                        >
                            <Trash2 className="w-4 h-4" />
                        </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
